import { Hidden } from '@material-ui/core';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';
import { GuidedProcessSupportBlock } from '../../../../../global/components/guided-process-support-block';
import { DividerSection } from '../../../../../global/components/layout/divider-section';
import { PageHeader } from '../../../../../global/components/layout/page-header';
import { SideBarContent } from '../../../../../global/components/layout/sidebar-content';
import { apiErrorClear } from '../../../../../global/ducks/base-api';
import { getGpsType } from '../../../../../global/helpers/gps-helper';
import { useLockedPageState } from '../../../../../global/hooks/useLockedPageState';
import { useParentRoute } from '../../../../../global/hooks/useParentRoute';
import { ApiStatuses } from '../../../../../global/models/api-model';
import { LockedPageKeys } from '../../../../../global/models/locked-page-model';
import { TcsmEpiApiState, TcsmStates } from '../../../../../root/tcsm-model';
import { FormTypes } from '../../../../account-info/models/form-models';
import { EmploymentInfo } from '../../compensation/individual-compensation/employment-info';
import { EmploymentState, Position, PositionTypes } from '../employment-model';
import { getCurrentEmploymentItem, getEmploymentItemById } from '../employment-selectors';
import { setUpdateNonStipPositionStatus, setUpdateStipPositionStatus } from '../employment-slice';
import { PositionForm } from './position-form';
import { PositionFormValues } from './position-form-model';
import { patchNonStipPositionRequest, patchStipPositionRequest, postNonStipEmploymentRequest, postNonStipPositionRequest, postStipPositionRequest } from './position-requests';

export function Positions() {
	const epiProps = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const employmentState = useSelector<TcsmStates, EmploymentState>(state => state.employmentState);
	const employerId = useSelector<TcsmStates, string>(state => state.institutionState?.institutionClientNumber);
	const parentRoute = useParentRoute();
	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(apiErrorClear());
		dispatch(setUpdateStipPositionStatus(ApiStatuses.None));
		dispatch(setUpdateNonStipPositionStatus(ApiStatuses.None));
	}, []);

	const { state } = useLocation<any>();

	const lockedState = useLockedPageState(LockedPageKeys.Employment);
	const gpsItem = getGpsType(null, null);


	const positionType: PositionTypes = state?.positionType;
	const positionId: string = state?.positionId;

	const currentEmployment = positionType === PositionTypes.Stipendiary ? employmentState?.currentEmployment : employmentState?.nonStipendiaryEmployment;

	const currentPosition = (positionId
		? getEmploymentItemById(currentEmployment?.position_titles, positionId)
		: getCurrentEmploymentItem(currentEmployment?.position_titles)) as Position;

	const firstNonStip = positionType === PositionTypes.NonStipendiary && !currentEmployment;

	const handleSubmit = (initialValues: PositionFormValues, formValues: PositionFormValues) => {
		const employeeId = employmentState?.currentEmployee?.id;
		const employmentId = positionType === PositionTypes.Stipendiary ? currentEmployment?.id : currentEmployment?.non_stipendiary_position_id;

		if (positionType === PositionTypes.Stipendiary) {
			if (positionId) {
				patchStipPositionRequest(dispatch, employeeId, employmentId, initialValues, formValues, currentPosition);
			} else {
				postStipPositionRequest(dispatch, employeeId, employmentId, initialValues, formValues, currentPosition);
			}
		} else if (positionType === PositionTypes.NonStipendiary) {
			if (firstNonStip) {
				postNonStipEmploymentRequest(dispatch, employeeId, employerId, formValues);
			} else if (positionId) {
				patchNonStipPositionRequest(dispatch, employeeId, employmentId, initialValues, formValues, currentPosition);
			} else {
				postNonStipPositionRequest(dispatch, employeeId, employmentId, currentEmployment?.employer_client_number, initialValues, formValues);
			}
		}
	};

	const employmentProps = epiProps?.employmentSettingsState?.epiContent;

	let pageTitle: string;
	let pageDescription: string;

	if (positionType === PositionTypes.Stipendiary) {
		pageTitle = employmentProps?.editStipPositionTitle;
		pageDescription = employmentProps?.editStipPositionDescription;
	} else if (positionType === PositionTypes.NonStipendiary) {
		pageTitle = positionId ? employmentProps?.editNonStipTitle : employmentProps?.addNonStipTitle;
		pageDescription = positionId ? employmentProps?.editNonStipPositionDescription : employmentProps?.addNonStipPositionDescription;
	}


	// redirect to individual compensation page on success with query string
	if (employmentState?.updateStipPositionStatus === ApiStatuses.Success || employmentState?.updateNonstipPositionStatus === ApiStatuses.Success) {
		return (<Redirect to={{
			pathname: `${parentRoute}`,
			search: `?success=${employmentState?.updateStipPositionStatus === ApiStatuses.Success ? FormTypes.STIP_POSITION : FormTypes.NON_STIP_POSITION}`,
		}} />);
	}

	// Leaving this comment here in case there are rules for stip vs non-stip
	if (lockedState.isLocked /*unpaidLeave && positionType === PositionTypes.Stipendiary*/) {
		return (<Redirect to={{
			pathname: `${parentRoute}`,
		}} />);
	}

	return (
		<>

			<PositionForm
				onSubmitHandler={handleSubmit}
				currentPosition={currentPosition}
				currentHireDate={currentEmployment?.hire_date}
				isEdit={!!positionId}
				previousRoute={parentRoute}
				employmentProps={employmentProps}
				labelSettings={epiProps?.labelSettingState?.epiContent}
				positionType={positionType}
				firstNonStip={firstNonStip}
			>
				<PageHeader
					headerText={pageTitle}
					description={pageDescription} />
				<Hidden mdUp>
					<SideBarContent>
						<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
					</SideBarContent>
				</Hidden>
				<DividerSection>
					<EmploymentInfo
						employmentData={currentEmployment || employmentState?.currentEmployment} // will try to read non-stip employment but falls back on stip employment
					/>
				</DividerSection>
			</PositionForm>
		</>
	);
}